// src/pages/QBODashboardPage.tsx
// QBO Sync Dashboard — migrated from standalone qbo-sync-full app
// Converted to TypeScript + Tailwind CSS to match advantage-frontend styling

import { useState, useEffect } from 'react';
import { fmtLocalTime, fmtLocalTimeOr } from '../lib/formatTime';
import { ArrowLeft, RefreshCw, Link2, Unlink, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import qboApi from '../services/qboApi';
import WriteBackCard from './qbo/WriteBackCard';
import ReviewPushPanel from './qbo/ReviewPushPanel';
import ConversionTab from './qbo/ConversionTab';
import AuditTab from './qbo/AuditTab';
import { isoPlusDays } from './qbo/qboTypes';

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

interface QBODashboardPageProps {
  onBack: () => void;
}

interface QBOStatus {
  connected: boolean;
  realmId?: string;
  accessTokenValid?: boolean;
  expiresAt?: string;
}

interface Message {
  type: 'error' | 'success' | 'info';
  text: string;
}

interface SummaryRow {
  TableName: string;
  Total: number;
  SuccessCount: number;
  FailedCount: number;
  LastSync: string | null;
}

interface SyncDetail {
  status: string;
  action?: string;
  qboId?: string;
  total?: number;
  error?: string;
  invoice?: string;
  receipt?: string;
  po?: string;
  code?: string;
  name?: string;
}

interface SyncResultData {
  duration?: string;
  // v3 .NET agent: flat counts + details
  created?: number;
  updated?: number;
  errors?: number;
  skipped?: number;
  details?: SyncDetail[];
  // v2 bridge shape (kept for older agents)
  new?: { created?: number; updated?: number; errors?: number; details?: SyncDetail[] };
  modified?: { details?: SyncDetail[] };
}

interface ResendResultData {
  status: string;
  action?: string;
  qboId?: string;
  error?: string;
}

interface ActivityRow {
  TableName: string;
  RecordKey: string;
  SyncAction: string;
  QBOEntityID: string;
  SyncStatus: string;
  LastSyncDate: string;
}

interface ErrorRow {
  TableName: string;
  RecordKey: string;
  ErrorMessage: string;
  RetryCount: number;
  LastSyncDate: string;
}

interface Column {
  key: string;
  label: string;
  render?: (row: Record<string, unknown>) => React.ReactNode;
}

type TabId = 'overview' | 'conversion' | 'customers' | 'invoices' | 'payments' | 'purchases' | 'vendors' | 'audit' | 'activity' | 'errors';

// ═══════════════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════════════

function statusBgClass(s: string): string {
  const status = (s || '').trim();
  if (status === 'Success') return 'bg-green-900/60 text-green-300';
  if (status === 'Failed') return 'bg-red-900/60 text-red-300';
  if (status === 'Retry') return 'bg-amber-900/60 text-amber-300';
  return 'bg-slate-700 text-slate-300';
}

function Badge({ synced, qboId }: { synced?: boolean; qboId?: string }) {
  return synced ? (
    <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold bg-green-900/60 text-green-300">
      Synced{qboId ? ` (QBO:${qboId})` : ''}
    </span>
  ) : (
    <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold bg-blue-900/60 text-blue-300">
      New
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════════════

export default function QBODashboardPage({ onBack }: QBODashboardPageProps) {
  const [tab, setTab] = useState<TabId>('overview');
  // Day after the configured conversion cutoff: the earliest date a detailed
  // invoice / payment push may start from (null = no cutoff configured).
  const [afterCutoff, setAfterCutoff] = useState<string | null>(null);
  const [qbo, setQbo] = useState<QBOStatus | null>(null);
  const [summary, setSummary] = useState<SummaryRow[] | null>(null);
  const [msg, setMsg] = useState<Message | null>(null);
  const [loading, setLoading] = useState<Record<string, boolean>>({});

  // Entity previews
  const [custPreview, setCustPreview] = useState<unknown>(null);
  const [invPreview, setInvPreview] = useState<unknown>(null);
  const [payPreview, setPayPreview] = useState<unknown>(null);
  const [purPreview, setPurPreview] = useState<unknown>(null);

  const [syncResult, setSyncResult] = useState<SyncResultData | null>(null);
  const [resendKey, setResendKey] = useState('');
  const [resendResult, setResendResult] = useState<ResendResultData | null>(null);
  const [activity, setActivity] = useState<ActivityRow[] | null>(null);
  const [errors, setErrors] = useState<ErrorRow[] | null>(null);

  const load = (k: string, v: boolean) => setLoading((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (msg) {
      const t = setTimeout(() => setMsg(null), 8000);
      return () => clearTimeout(t);
    }
  }, [msg]);

  useEffect(() => {
    loadStatus();
    loadSummary();
    qboApi.conversionConfig()
      .then((c: { cutoff?: string | null; cutoffExceptions?: string[] }) => {
        // Earliest pushable day: the day after the cutoff, or an exception day
        // (3.18.7: a day with no legacy journal, pushed in full) if one is earlier.
        const after = c?.cutoff ? isoPlusDays(c.cutoff, 1) : null;
        const firstException = (c?.cutoffExceptions ?? []).filter(Boolean).sort()[0];
        setAfterCutoff(firstException && (!after || firstException < after) ? firstException : after);
      })
      .catch(() => setAfterCutoff(null));
    // Outcome of the OAuth round-trip, left here by App.tsx when the agent
    // redirected the browser back to /oauth-success or /oauth-error.
    try {
      const raw = sessionStorage.getItem('qboOAuthResult');
      if (raw) {
        sessionStorage.removeItem('qboOAuthResult');
        const r = JSON.parse(raw) as { ok: boolean; message?: string };
        setMsg(r.ok
          ? { type: 'success', text: 'QuickBooks Online connected' }
          : { type: 'error', text: `QuickBooks connection failed: ${r.message || 'unknown error'}` });
      }
    } catch { /* ignore */ }
  }, []);

  // ── Data loaders ──

  const loadStatus = async () => {
    try { setQbo(await qboApi.getQBOStatus()); } catch { setQbo({ connected: false }); }
  };
  const loadSummary = async () => {
    try { setSummary(await qboApi.getSummary()); } catch { /* ignore */ }
  };
  const loadActivity = async () => {
    try { load('act', true); setActivity(await qboApi.getActivity(100)); }
    catch (e: unknown) { setMsg({ type: 'error', text: (e as Error).message }); }
    finally { load('act', false); }
  };
  const loadErrors = async () => {
    try { setErrors(await qboApi.getErrors()); }
    catch (e: unknown) { setMsg({ type: 'error', text: (e as Error).message }); }
  };

  // ── QBO Connection ──

  const startOAuth = async () => {
    try {
      load('oauth', true);
      const { authUrl } = await qboApi.getAuthUrl();
      window.open(authUrl, '_blank', 'width=600,height=700');
    } catch (e: unknown) { setMsg({ type: 'error', text: (e as Error).message }); }
    finally { load('oauth', false); }
  };

  const testConn = async () => {
    try {
      load('test', true);
      const r = await qboApi.testConnection();
      setMsg({ type: 'success', text: `Connected: ${r.company}` });
    } catch (e: unknown) { setMsg({ type: 'error', text: (e as Error).message }); }
    finally { load('test', false); }
  };

  const refreshTok = async () => {
    try {
      load('refresh', true);
      await qboApi.refreshToken();
      await loadStatus();
      setMsg({ type: 'success', text: 'Token refreshed' });
    } catch (e: unknown) { setMsg({ type: 'error', text: (e as Error).message }); }
    finally { load('refresh', false); }
  };

  // ── Generic sync ──

  const runSync = async (key: string, syncFn: () => Promise<SyncResultData>, label: string) => {
    try {
      load(key, true);
      setSyncResult(null);
      const r = await syncFn();
      setSyncResult(r);
      await loadSummary();
      const created = r.created ?? r.new?.created ?? 0;
      const updated = r.updated ?? r.new?.updated ?? 0;
      const errs = r.errors ?? r.new?.errors ?? 0;
      setMsg({
        type: errs ? 'error' : 'success',
        text: `${label}: ${created} created, ${updated} updated, ${errs} errors`,
      });
    } catch (e: unknown) { setMsg({ type: 'error', text: (e as Error).message }); }
    finally { load(key, false); }
  };

  const doResend = async (key: string, resendFn: (k: string) => Promise<ResendResultData>, label: string) => {
    if (!resendKey.trim()) return;
    try {
      load(key, true);
      setResendResult(null);
      const raw = await resendFn(resendKey.trim()) as ResendResultData & { errors?: number; details?: Array<{ status?: string; action?: string; qboId?: string; error?: string; warnings?: string[] }> };
      // The invoice / payment engines answer with a full sync result (created / updated / details); fold it into one line.
      const d = raw.details?.[0];
      const r: ResendResultData = raw.status ? raw
        : (raw.errors ?? 0) > 0 || d?.status === 'error'
          ? { status: 'error', error: d?.error ?? 'resend failed' }
          : { status: 'success', action: d?.action ?? 'Resend', qboId: d?.qboId ?? '', error: d?.warnings?.join(' | ') };
      setResendResult(r);
      if (r.status === 'success') setMsg({ type: 'success', text: `${label} ${resendKey} → ${r.action} QBO ${r.qboId}${r.error ? ` (${r.error})` : ''}` });
      else setMsg({ type: 'error', text: r.error ?? 'resend failed' });
    } catch (e: unknown) {
      setResendResult({ status: 'error', error: (e as Error).message });
      setMsg({ type: 'error', text: (e as Error).message });
    } finally { load(key, false); }
  };

  // ── Tab switching ──

  const switchTab = (t: TabId) => {
    setTab(t);
    setSyncResult(null);
    setResendResult(null);
    setResendKey('');
    if (t === 'activity') loadActivity();
    if (t === 'errors') loadErrors();
  };

  // ── Preview Table ──

  function PreviewTable({ data, columns, keyField }: { data: unknown; columns: Column[]; keyField: string }) {
    if (!data) return null;
    const d = data as Record<string, unknown[]>;
    const items = (d.customers || d.invoices || d.payments || d.purchases || []) as Record<string, unknown>[];
    if (items.length === 0) return <div className="text-slate-500 text-sm">No records found</div>;
    return (
      <div className="overflow-x-auto mt-3">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} className="text-left px-3 py-2 border-b border-slate-700 text-slate-500 font-semibold text-[11px] uppercase">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((row, i) => (
              <tr key={(row[keyField] as string) || i} className="hover:bg-slate-800/50">
                {columns.map((c) => (
                  <td key={c.key} className="px-3 py-2 border-b border-slate-800/60 text-slate-300">
                    {c.render
                      ? c.render(row)
                      : typeof row[c.key] === 'boolean'
                        ? row[c.key] ? '✓' : ''
                        : (row[c.key] as string) ?? ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-2 text-xs text-slate-500">{items.length} records</div>
      </div>
    );
  }

  // ── Sync Results ──

  function SyncResults() {
    if (!syncResult) return null;
    const details = syncResult.details || syncResult.new?.details || syncResult.modified?.details || [];
    return (
      <div className="mt-4 bg-slate-800 rounded-xl p-5 border border-slate-700">
        <div className="text-sm font-semibold text-slate-400 mb-3 flex items-center gap-2">
          <RefreshCw size={14} /> Sync Results ({syncResult.duration})
        </div>
        <div className="bg-slate-900 rounded-lg p-3 max-h-[300px] overflow-y-auto text-xs font-mono text-slate-400">
          {details.map((d, i) => (
            <div key={i} className={`mb-1 ${d.status === 'success' ? 'text-green-400' : 'text-red-400'}`}>
              {d.status === 'success' ? '✓' : '✗'}{' '}
              {d.invoice || d.receipt || d.po || d.code || d.name} —{' '}
              {d.status === 'success'
                ? `${d.action} → QBO: ${d.qboId}${d.total ? ` ($${d.total})` : ''}`
                : d.error}
            </div>
          ))}
          {details.length === 0 && <div>No changes detected</div>}
        </div>
      </div>
    );
  }

  // ── Entity Panel ──
  // PreviewTable / SyncResults / EntityPanel are closures over this page's
  // state, so they are RENDERED AS FUNCTION CALLS, never as <Components>:
  // a nested component gets a new identity on every render, React remounts
  // its subtree, and the Manual Resend input lost focus after each keystroke.

  function EntityPanel({
    label, keyLabel, previewData, setPreviewData, loadPreviewFn, syncFn, resendFn, columns, keyField,
  }: {
    label: string;
    keyLabel: string;
    previewData: unknown;
    setPreviewData: (d: unknown) => void;
    loadPreviewFn: () => Promise<unknown>;
    syncFn: () => Promise<SyncResultData>;
    resendFn: (k: string) => Promise<ResendResultData>;
    columns: Column[];
    keyField: string;
  }) {
    return (
      <div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Sync card */}
          <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
            <div className="text-sm font-semibold text-slate-400 mb-3">{label} Sync</div>
            <div className="flex flex-wrap gap-2">
              <button
                className="px-4 py-2 rounded-lg text-sm font-medium border border-slate-600 text-slate-300 hover:bg-slate-700 transition-colors disabled:opacity-50"
                onClick={async () => {
                  load(`pv-${label}`, true);
                  try { setPreviewData(await loadPreviewFn()); }
                  catch (e: unknown) { setMsg({ type: 'error', text: (e as Error).message }); }
                  finally { load(`pv-${label}`, false); }
                }}
                disabled={loading[`pv-${label}`]}
              >
                {loading[`pv-${label}`] ? 'Loading...' : 'Preview'}
              </button>
              <button
                className="px-4 py-2 rounded-lg text-sm font-medium bg-green-600 text-white hover:bg-green-500 transition-colors disabled:opacity-50"
                onClick={() => runSync(`sync-${label}`, syncFn, label)}
                disabled={loading[`sync-${label}`]}
              >
                {loading[`sync-${label}`] ? 'Syncing...' : `Sync ${label}`}
              </button>
            </div>
          </div>

          {/* Manual resend card */}
          <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
            <div className="text-sm font-semibold text-slate-400 mb-3">Manual Resend</div>
            <div className="flex flex-wrap gap-2 items-center">
              <input
                className="px-3 py-2 rounded-lg border border-slate-600 bg-slate-900 text-slate-200 text-sm w-36 focus:outline-none focus:border-blue-500"
                placeholder={keyLabel}
                value={resendKey}
                onChange={(e) => setResendKey(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && doResend(`rs-${label}`, resendFn, label)}
              />
              <button
                className="px-4 py-2 rounded-lg text-sm font-medium bg-amber-500 text-black hover:bg-amber-400 transition-colors disabled:opacity-50"
                onClick={() => doResend(`rs-${label}`, resendFn, label)}
                disabled={loading[`rs-${label}`] || !resendKey.trim()}
              >
                {loading[`rs-${label}`] ? 'Sending...' : 'Resend'}
              </button>
            </div>
            {resendResult && (
              <div className={`mt-2 text-xs ${resendResult.status === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                {resendResult.status === 'success'
                  ? `✓ ${resendResult.action} → QBO: ${resendResult.qboId}`
                  : `✗ ${resendResult.error}`}
              </div>
            )}
          </div>
        </div>

        {PreviewTable({ data: previewData, columns, keyField })}
        {SyncResults()}
      </div>
    );
  }

  // ── Column definitions ──

  const custCols: Column[] = [
    { key: 'code', label: 'Code' },
    { key: 'name', label: 'Name' },
    { key: 'city', label: 'City' },
    { key: 'state', label: 'ST' },
    { key: 'entered', label: 'Entered', render: (r) => r.entered ? new Date(r.entered as string).toLocaleDateString() : '' },
    { key: 'synced', label: 'Status', render: (r) => <Badge synced={r.alreadySynced as boolean} qboId={r.qboId as string} /> },
  ];
  const invCols: Column[] = [
    { key: 'invoice', label: 'Invoice' },
    { key: 'date', label: 'Date', render: (r) => r.date ? new Date(r.date as string).toLocaleDateString() : '' },
    { key: 'name', label: 'Customer' },
    { key: 'total', label: 'Total', render: (r) => `$${((r.total as number) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
    { key: 'balance', label: 'Balance', render: (r) => `$${((r.balance as number) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
    { key: 'terms', label: 'Terms' },
    { key: 'custOk', label: 'Cust', render: (r) => (r.customerInQbo as boolean) ? <CheckCircle2 size={14} className="text-green-400" /> : <XCircle size={14} className="text-red-400" /> },
    { key: 'synced', label: 'Status', render: (r) => <Badge synced={r.alreadySynced as boolean} /> },
  ];
  const payCols: Column[] = [
    { key: 'receipt', label: 'Receipt#' },
    { key: 'date', label: 'Date', render: (r) => r.date ? new Date(r.date as string).toLocaleDateString() : '' },
    { key: 'name', label: 'Customer' },
    { key: 'amount', label: 'Amount', render: (r) => `$${((r.amount as number) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
    { key: 'checkNo', label: 'Check#' },
    { key: 'invoiceCount', label: 'Invoices' },
    { key: 'synced', label: 'Status', render: (r) => <Badge synced={r.alreadySynced as boolean} /> },
  ];
  const purCols: Column[] = [
    { key: 'po', label: 'PO#' },
    { key: 'date', label: 'Date', render: (r) => r.date ? new Date(r.date as string).toLocaleDateString() : '' },
    { key: 'name', label: 'Vendor' },
    { key: 'total', label: 'Total', render: (r) => `$${((r.total as number) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
    { key: 'balance', label: 'Balance', render: (r) => `$${((r.balance as number) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
    { key: 'synced', label: 'Status', render: (r) => <Badge synced={r.alreadySynced as boolean} /> },
  ];

  const TABS: { id: TabId; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'conversion', label: 'Conversion' },
    { id: 'customers', label: 'Customers' },
    { id: 'invoices', label: 'Invoices' },
    { id: 'payments', label: 'Payments' },
    { id: 'purchases', label: 'Purchases' },
    { id: 'vendors', label: 'Vendors' },
    { id: 'audit', label: 'Audit' },
    { id: 'activity', label: 'Activity' },
    { id: 'errors', label: 'Errors' },
  ];

  const notify = (type: Message['type'], text: string) => setMsg({ type, text });

  // ═══════════════════════════════════════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════════════════════════════════════

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-200">
      {/* Header */}
      <header className="border-b border-slate-700/50 bg-slate-900/80 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <button
                onClick={onBack}
                className="p-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-slate-400 hover:text-white hover:border-slate-600 transition-all"
                title="Back to dashboard"
              >
                <ArrowLeft size={16} />
              </button>
              <div>
                <h1 className="text-base font-bold text-white tracking-tight">QBO Sync Dashboard</h1>
                <p className="text-[11px] text-slate-500 -mt-0.5">Advantage Database → QuickBooks Online</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${qbo?.connected ? 'bg-green-500' : 'bg-red-500'}`} />
              <span className={`text-sm ${qbo?.connected ? 'text-green-400' : 'text-red-400'}`}>
                {qbo?.connected ? 'QBO Connected' : 'QBO Disconnected'}
              </span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Alert */}
        {msg && (
          <div
            className={`px-4 py-3 rounded-lg mb-4 text-sm border ${
              msg.type === 'error'
                ? 'bg-red-950/60 border-red-800 text-red-200'
                : msg.type === 'success'
                  ? 'bg-green-950/60 border-green-800 text-green-200'
                  : 'bg-blue-950/60 border-blue-800 text-blue-200'
            }`}
          >
            {msg.text}
          </div>
        )}

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 mb-5">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => switchTab(t.id)}
              className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
                tab === t.id
                  ? 'bg-slate-800 text-white border-b-2 border-blue-500'
                  : 'text-slate-500 hover:text-slate-300 border-b-2 border-transparent'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ═══ OVERVIEW ═══ */}
        {tab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="md:col-span-2"><WriteBackCard onMessage={notify} /></div>
            {/* QBO Connection card */}
            <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
              <div className="text-sm font-semibold text-slate-400 mb-3 flex items-center gap-2">
                {qbo?.connected ? <Link2 size={14} /> : <Unlink size={14} />} QuickBooks Connection
              </div>
              {qbo?.connected ? (
                <div>
                  <div className="mb-2 text-sm">
                    Realm: <span className="text-white">{qbo.realmId}</span>
                  </div>
                  <div className="mb-3 text-sm">
                    Token:{' '}
                    <span className={qbo.accessTokenValid ? 'text-green-400' : 'text-red-400'}>
                      {qbo.accessTokenValid ? 'Valid' : 'Expired'}
                    </span>
                    {qbo.expiresAt && (
                      <span className="text-slate-500 ml-1">({new Date(qbo.expiresAt).toLocaleString()})</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      className="px-3 py-1.5 rounded-lg text-sm border border-slate-600 text-slate-300 hover:bg-slate-700 transition-colors disabled:opacity-50"
                      onClick={testConn}
                      disabled={loading.test}
                    >
                      {loading.test ? '...' : 'Test'}
                    </button>
                    <button
                      className="px-3 py-1.5 rounded-lg text-sm border border-slate-600 text-slate-300 hover:bg-slate-700 transition-colors disabled:opacity-50"
                      onClick={refreshTok}
                      disabled={loading.refresh}
                    >
                      {loading.refresh ? '...' : 'Refresh'}
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-slate-400 mb-3">Connect to QuickBooks to enable syncing.</p>
                  <button
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-500 transition-colors disabled:opacity-50"
                    onClick={startOAuth}
                    disabled={loading.oauth}
                  >
                    {loading.oauth ? '...' : 'Connect to QuickBooks'}
                  </button>
                </div>
              )}
            </div>

            {/* Sync Summary card */}
            <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
              <div className="text-sm font-semibold text-slate-400 mb-3 flex items-center gap-2">
                <RefreshCw size={14} /> Sync Summary
              </div>
              {summary && Array.isArray(summary) && summary.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr>
                        <th className="text-left px-3 py-2 border-b border-slate-700 text-slate-500 font-semibold text-[11px] uppercase">Entity</th>
                        <th className="text-left px-3 py-2 border-b border-slate-700 text-slate-500 font-semibold text-[11px] uppercase">Total</th>
                        <th className="text-left px-3 py-2 border-b border-slate-700 text-slate-500 font-semibold text-[11px] uppercase">Success</th>
                        <th className="text-left px-3 py-2 border-b border-slate-700 text-slate-500 font-semibold text-[11px] uppercase">Failed</th>
                        <th className="text-left px-3 py-2 border-b border-slate-700 text-slate-500 font-semibold text-[11px] uppercase">Last Sync</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.map((s, i) => (
                        <tr key={i} className="hover:bg-slate-700/30">
                          <td className="px-3 py-2 border-b border-slate-800/60 text-slate-300">{(s.TableName || '').trim()}</td>
                          <td className="px-3 py-2 border-b border-slate-800/60 text-slate-300">{s.Total}</td>
                          <td className="px-3 py-2 border-b border-slate-800/60 text-green-400">{s.SuccessCount}</td>
                          <td className={`px-3 py-2 border-b border-slate-800/60 ${s.FailedCount > 0 ? 'text-red-400' : 'text-green-400'}`}>{s.FailedCount}</td>
                          <td className="px-3 py-2 border-b border-slate-800/60 text-slate-400 font-mono text-xs">
                            {fmtLocalTimeOr(s.LastSync, '—')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-slate-500 text-sm">No sync data yet. Run a sync to get started.</div>
              )}
              <button
                className="mt-3 px-3 py-1.5 rounded-lg text-sm border border-slate-600 text-slate-300 hover:bg-slate-700 transition-colors"
                onClick={loadSummary}
              >
                Refresh
              </button>
            </div>
          </div>
        )}

        {/* ═══ CONVERSION (open receivables at the cutoff) ═══ */}
        {tab === 'conversion' && <ConversionTab onMessage={notify} onRan={loadSummary} />}
        {tab === 'audit' && <AuditTab onMessage={notify} />}

        {/* ═══ ENTITY TABS — review by date range + push a selection (3.17.0), legacy quick-sync below ═══ */}
        {tab === 'customers' && (
          <>
            <ReviewPushPanel entity="customers" label="Customers" onMessage={notify} onPushed={loadSummary} />
            {EntityPanel({
              label: 'Customers', keyLabel: 'Customer Code',
              previewData: custPreview, setPreviewData: setCustPreview,
              loadPreviewFn: () => qboApi.previewCustomers(),
              syncFn: () => qboApi.syncCustomers(),
              resendFn: (k) => qboApi.resendCustomer(k),
              columns: custCols, keyField: 'code',
            })}
          </>
        )}
        {tab === 'invoices' && (
          <>
            <ReviewPushPanel entity="invoices" label="Invoices" onMessage={notify} onPushed={loadSummary} minFrom={afterCutoff} />
            {EntityPanel({
              label: 'Invoices', keyLabel: 'Invoice#',
              previewData: invPreview, setPreviewData: setInvPreview,
              loadPreviewFn: () => qboApi.previewInvoices(),
              syncFn: () => qboApi.syncInvoices(),
              resendFn: (k) => qboApi.resendInvoice(k),
              columns: invCols, keyField: 'invoice',
            })}
          </>
        )}
        {tab === 'payments' && (
          <>
            {/* From is clamped to the day after the cutoff: since agent 3.18.17 a day lists that day's Advantage cash journal (by cash date), so earlier days are never needed; a pre-cutoff exception day is handled by hand. */}
            <ReviewPushPanel entity="payments" label="Payments" onMessage={notify} onPushed={loadSummary} minFrom={afterCutoff} />
            {EntityPanel({
              label: 'Payments', keyLabel: 'Receipt#',
              previewData: payPreview, setPreviewData: setPayPreview,
              loadPreviewFn: () => qboApi.previewPayments(),
              syncFn: () => qboApi.syncPayments(),
              resendFn: (k) => qboApi.resendPayment(k),
              columns: payCols, keyField: 'receipt',
            })}
          </>
        )}
        {tab === 'purchases' && (
          <>
            <ReviewPushPanel entity="purchases" label="Purchases" onMessage={notify} onPushed={loadSummary} />
            {EntityPanel({
              label: 'Purchases', keyLabel: 'PO#',
              previewData: purPreview, setPreviewData: setPurPreview,
              loadPreviewFn: () => qboApi.previewPurchases(),
              syncFn: () => qboApi.syncPurchases(),
              resendFn: (k) => qboApi.resendPurchase(k),
              columns: purCols, keyField: 'po',
            })}
          </>
        )}
        {tab === 'vendors' && (
          <ReviewPushPanel entity="vendors" label="Vendors" onMessage={notify} onPushed={loadSummary} />
        )}

        {/* ═══ ACTIVITY ═══ */}
        {tab === 'activity' && (
          <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-slate-400">Recent Sync Activity</div>
              <button
                className="px-3 py-1.5 rounded-lg text-sm border border-slate-600 text-slate-300 hover:bg-slate-700 transition-colors disabled:opacity-50"
                onClick={loadActivity}
                disabled={loading.act}
              >
                {loading.act ? '...' : 'Refresh'}
              </button>
            </div>
            {activity && Array.isArray(activity) && activity.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr>
                      <th className="text-left px-3 py-2 border-b border-slate-700 text-slate-500 font-semibold text-[11px] uppercase">Entity</th>
                      <th className="text-left px-3 py-2 border-b border-slate-700 text-slate-500 font-semibold text-[11px] uppercase">Key</th>
                      <th className="text-left px-3 py-2 border-b border-slate-700 text-slate-500 font-semibold text-[11px] uppercase">Action</th>
                      <th className="text-left px-3 py-2 border-b border-slate-700 text-slate-500 font-semibold text-[11px] uppercase">QBO ID</th>
                      <th className="text-left px-3 py-2 border-b border-slate-700 text-slate-500 font-semibold text-[11px] uppercase">Status</th>
                      <th className="text-left px-3 py-2 border-b border-slate-700 text-slate-500 font-semibold text-[11px] uppercase">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activity.map((a, i) => (
                      <tr key={i} className="hover:bg-slate-700/30">
                        <td className="px-3 py-2 border-b border-slate-800/60 text-slate-300">{(a.TableName || '').trim()}</td>
                        <td className="px-3 py-2 border-b border-slate-800/60 text-slate-300 font-mono text-xs">{(a.RecordKey || '').trim()}</td>
                        <td className="px-3 py-2 border-b border-slate-800/60 text-slate-300">{(a.SyncAction || '').trim()}</td>
                        <td className="px-3 py-2 border-b border-slate-800/60 text-slate-300 font-mono text-xs">{(a.QBOEntityID || '').trim()}</td>
                        <td className="px-3 py-2 border-b border-slate-800/60">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${statusBgClass((a.SyncStatus || '').trim())}`}>
                            {(a.SyncStatus || '').trim()}
                          </span>
                        </td>
                        <td className="px-3 py-2 border-b border-slate-800/60 text-slate-400 font-mono text-xs">
                          {fmtLocalTime(a.LastSyncDate)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-slate-500 text-sm">No activity yet</div>
            )}
          </div>
        )}

        {/* ═══ ERRORS ═══ */}
        {tab === 'errors' && (
          <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-slate-400 flex items-center gap-2">
                <AlertTriangle size={14} /> Errors & Retries
              </div>
              <button
                className="px-3 py-1.5 rounded-lg text-sm border border-slate-600 text-slate-300 hover:bg-slate-700 transition-colors"
                onClick={loadErrors}
              >
                Refresh
              </button>
            </div>
            {errors && Array.isArray(errors) && errors.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr>
                      <th className="text-left px-3 py-2 border-b border-slate-700 text-slate-500 font-semibold text-[11px] uppercase">Entity</th>
                      <th className="text-left px-3 py-2 border-b border-slate-700 text-slate-500 font-semibold text-[11px] uppercase">Key</th>
                      <th className="text-left px-3 py-2 border-b border-slate-700 text-slate-500 font-semibold text-[11px] uppercase">Error</th>
                      <th className="text-left px-3 py-2 border-b border-slate-700 text-slate-500 font-semibold text-[11px] uppercase">Retries</th>
                      <th className="text-left px-3 py-2 border-b border-slate-700 text-slate-500 font-semibold text-[11px] uppercase">Date</th>
                      <th className="text-left px-3 py-2 border-b border-slate-700 text-slate-500 font-semibold text-[11px] uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {errors.map((e, i) => (
                      <tr key={i} className="hover:bg-slate-700/30">
                        <td className="px-3 py-2 border-b border-slate-800/60 text-slate-300">{(e.TableName || '').trim()}</td>
                        <td className="px-3 py-2 border-b border-slate-800/60 text-slate-300 font-mono text-xs">{(e.RecordKey || '').trim()}</td>
                        <td
                          className="px-3 py-2 border-b border-slate-800/60 text-red-400 max-w-[300px] overflow-hidden text-ellipsis whitespace-nowrap"
                          title={(e.ErrorMessage || '').trim()}
                        >
                          {(e.ErrorMessage || '').trim()}
                        </td>
                        <td className="px-3 py-2 border-b border-slate-800/60 text-slate-300">{e.RetryCount}</td>
                        <td className="px-3 py-2 border-b border-slate-800/60 text-slate-400 font-mono text-xs">
                          {fmtLocalTime(e.LastSyncDate)}
                        </td>
                        <td className="px-3 py-2 border-b border-slate-800/60">
                          <button
                            className="px-2.5 py-1 rounded-lg text-[11px] font-medium bg-amber-500 text-black hover:bg-amber-400 transition-colors"
                            onClick={async () => {
                              try {
                                await qboApi.markResend((e.TableName || '').trim(), (e.RecordKey || '').trim());
                                setMsg({ type: 'success', text: 'Marked for resend' });
                                loadErrors();
                              } catch (err: unknown) {
                                setMsg({ type: 'error', text: (err as Error).message });
                              }
                            }}
                          >
                            Mark Resend
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-slate-500 text-sm">No errors</div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
